import React, {useState} from 'react'
import {GradientTabs, GradientTab} from '../../core/shared/GradientTabs'
import Icon from '../../core/shared/Icon'
import {getProductIcon} from '../../core/shared/ProductIcons'
import {calculateCouponsAmountForItem} from '../../utils/coupon-helpers'
import {NEW_SEO_PLAN, NEW_SEO_ADDONS} from '../../utils/constants'
import {
  isTgpFoundationFamilyCode,
  isThryvBasicCode,
} from '../../utils/tgp-catalog-flags'
import {VERTICAL_CLIENTSVCS_CODES} from '../CatalogGrid/utils/productSelectionRules'
import {
  Wrapper,
  Header,
  HeaderLeft,
  HeaderRight,
  SectionBlock,
  ItemRow,
  ItemInfo,
  ItemName,
  ItemSub,
  ItemSubRow,
  ItemDetailsToggle,
  ItemDetailsPanel,
  ItemDetailRow,
  ItemDetailLabel,
  ItemDetailValue,
  // ItemDetailAddonsHeading,
  ItemIcon,
  ItemPriceWrapper,
  ItemPrice,
  EmptyState,
  FooterRow,
  LocationAccordion,
  LocationAccordionWrapper,
  LocationHeader,
  LocationMeta,
  SubAccordion,
  SubAccordionHeader,
  SubAccordionMeta,
  SubAccordionTotal,
  ChevronIcon,
  RemoveButton,
  GstDisclaimer,
} from './OrderSummary.styles'

// Plans where unitPrice = total (unitAmount=1, quantity=$budget) — must not
// multiply unitPrice × quantity again in display. Mirrors orderSummaryUtils.js.
const QUANTITY_AS_DOLLAR_CODES = new Set([
  'tl-tax-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
])

// Mirrors legacy cart-item.js's showAddonPrice: every $0-priced add-on shows
// "Included" except this one — Leads Lift BNI is a real standalone product
// that can legitimately be priced $0 (a BNI-partner courtesy), not something
// bundled for free, so it always shows its actual price.
const LEADS_LIFT_BNI_CODE = 'addon-5-leadslift-bni-m2m'

const COUNTRY_LOCALE = {
  US: {locale: 'en-US', currency: 'USD'},
  AU: {locale: 'en-AU', currency: 'AUD'},
  CA: {locale: 'en-CA', currency: 'CAD'},
  NZ: {locale: 'en-NZ', currency: 'NZD'},
  GB: {locale: 'en-GB', currency: 'GBP'},
}

function formatCurrency(amount, country) {
  const n = Number(amount) || 0
  const {locale, currency} = COUNTRY_LOCALE[country] ?? COUNTRY_LOCALE.US
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

const DETAIL_LABELS = {
  proposalId: 'Proposal ID',
  externalDomain: 'External Domain',
  domain: 'Domain',
  proposalAmount: 'Proposal Amount',
  billingDifferentDate: 'Different Billing Date',
  billingDate: 'Billing Date',
  onboarding: 'Onboarding',
  eaid: 'EAID',
  includeOnboarding: 'Include Onboarding',
}

function parseConfig(raw) {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  if (Array.isArray(raw)) {
    return raw.reduce((acc, {name, value}) => {
      if (name) acc[name] = value
      return acc
    }, {})
  }
  return raw
}

// Maps an OrderSummary item to the shape expected by calculateCouponsAmountForItem
// prodType: 'plan' for software, 'itemBasedAddon' for add-ons, 'oneTimeFee' for OTFs
function getItemDiscount(item, coupons, prodType) {
  const {discountAmount} = calculateCouponsAmountForItem(
    {
      code: item.planCode ?? item.productCode ?? item.productId,
      prodType,
      currency: {unitAmount: item.price ?? item.unitPrice ?? 0},
    },
    coupons,
  )
  return discountAmount
}

export const DEFAULT_TABS = [
  {id: 'today', label: 'Due Today', suffix: '+ Tax', accent: false},
  {id: 'monthly', label: 'Due Monthly', suffix: '+ Tax*', accent: true},
]

export function OrderSummary({
  locations = [],
  total = 0,
  tabs = DEFAULT_TABS,
  singleLocation = false,
  country = 'US',
  // Distinguishes a brand-new sale from an add-on to an already-active
  // account (PurchaseLinkSlice's isUpgrade) — mirrors the legacy
  // `upgradePaymentPreview` flag that gated the AU "except SEO" GST caveat
  // below: SEO's GST-inclusive pricing only diverges from the displayed
  // total on a brand-new order, not on an upgrade preview.
  isUpgrade = false,
  // tab state
  activeTab,
  onTabChange,
  // accordion state
  openLocations = {},
  softwareOpen = {},
  addonsOpen = {},
  oneTimeFeeOpen = {},
  couponsOpen = {},
  onToggleLocation,
  onToggleSoftware,
  onToggleAddons,
  onToggleOneTimeFee,
  onToggleCoupons,
  // remove callbacks
  onRemoveSoftwareItem,
  onRemoveAddonItem,
  onRemoveOneTimeFeeItem,
  onRemoveCoupon,
  // applied coupons (global, shown in every location block)
  coupons = [],
  // optional per-tab item counts { [tabId]: number }; falls back to totalItems when absent
  tabItemCounts,
  // optional per-tab totals { [tabId]: number }; falls back to total when absent
  tabTotals,
  // optional footer slot (e.g. CouponSearch)
  footer,
  // PurchaseLink's Catalog Selection step wants the panel flush against the
  // step card's own right/bottom edges — ProposalTool's page (this
  // component's other caller) relies on the default -24px bleed to cancel
  // out its own container's padding, so this defaults to false there.
  flushEdges = false,
  // When the backend payment-preview was used, dueTodayAmount already includes
  // tax — suppress the "+ Tax" suffix on the Due Today tab in that case.
  dueTodayIncludesTax = false,
}) {
  const totalItems = locations.reduce((sum, l) => sum + l.itemCount, 0)
  const hasItems = totalItems > 0
  // AU/NZ plans are priced GST/tax-inclusive (see legacy payment-details.js
  // isAus/isNZ), so the generic "excludes taxes, fees and incentives" note
  // doesn't apply there — and AU orders need their own GST-inclusion
  // disclaimer, carved out for SEO (whose GST-inclusive pricing isn't
  // reflected in a brand-new order's displayed total the way it is on an
  // upgrade preview — see old-flow-desktop.js's SEO_GST_CODES).
  const isAus = country === 'AU'
  const isNZ = country === 'NZ'
  const isAusNZ = isAus || isNZ
  const seoCodes = [NEW_SEO_PLAN, ...NEW_SEO_ADDONS]
  const hasSeoItem = locations.some(loc =>
    [...(loc.softwareItems ?? []), ...(loc.addonItems ?? [])].some(item =>
      seoCodes.includes(item.productCode),
    ),
  )
  // Distinguishes "this location's data hasn't loaded yet" (a brand-new
  // account's just-provisioned free subscription hasn't been fetched yet)
  // from "this location genuinely has zero items" — without it a new
  // account's catalog step flashes "No Products Added" before its baseline
  // included items (My Account Basic+, Signatures Usage, Website Builder)
  // arrive a beat later.
  const isSingleLocationLoading =
    singleLocation && locations[0]?.isLoaded === false

  const [openAddonDetails, setOpenAddonDetails] = useState({})
  const toggleAddonDetail = key =>
    setOpenAddonDetails(prev => ({...prev, [key]: !prev[key]}))

  const couponDiscount = locations.reduce((acc, loc) => {
    const locCoupons = loc.coupons ?? coupons
    const sw = (loc.softwareItems ?? []).reduce(
      (s, item) => s + getItemDiscount(item, locCoupons, 'plan'),
      0,
    )
    const addon = (loc.addonItems ?? []).reduce(
      (s, item) => s + getItemDiscount(item, locCoupons, 'itemBasedAddon'),
      0,
    )
    const otf = (loc.oneTimeFeeItems ?? []).reduce(
      (s, item) => s + getItemDiscount(item, locCoupons, 'oneTimeFee'),
      0,
    )
    return acc + sw + addon + otf
  }, 0)
  const discountedTotal = Math.max(0, total - couponDiscount)

  // Legacy cc-desktop.js: "+ Tax" never applies to AU/NZ (GST/tax-inclusive
  // pricing); the trailing "*" footnote marker still applies to AU (and
  // everyone else) but not NZ.
  const getTabSuffix = tab => {
    if (!isAusNZ) return tab.suffix
    if (tab.id === 'today') return ''
    if (tab.id === 'monthly') return isAus ? '*' : ''
    return tab.suffix
  }

  const renderSubAccordions = (location, i) => {
    const locationCoupons = location.coupons ?? coupons
    return (
      <>
        <SubAccordion>
          <SubAccordionHeader onClick={() => onToggleSoftware?.(i)}>
            <SubAccordionMeta>
              <ChevronIcon open={softwareOpen[i]}>
                <Icon name="downArrow" size="xs" />
              </ChevronIcon>
              <div className="sub-category">
                Software <span>- {location.softwareItems.length} items</span>
              </div>
            </SubAccordionMeta>
            <SubAccordionTotal>
              {formatCurrency(
                location.softwareItems.reduce(
                  (s, it) => s + Number(it.price ?? 0),
                  0,
                ),
                country,
              )}
            </SubAccordionTotal>
          </SubAccordionHeader>
          {softwareOpen[i] && (
            <SectionBlock>
              {location.softwareItems.map((item, j) => {
                const itemAddons = item.addons || []
                return (
                  <React.Fragment key={j}>
                    <ItemRow selected={item.selected}>
                      <ItemIcon src={getProductIcon(item.name)} alt="" />
                      <ItemInfo>
                        <ItemName selected={item.selected}>
                          {item.name}
                        </ItemName>
                      </ItemInfo>
                      <ItemPriceWrapper>
                        <ItemPrice>
                          {item.price === null
                            ? ''
                            : (item.name?.toLowerCase().includes('basic') &&
                                !isThryvBasicCode(item.productCode)) ||
                              (item.alreadyIncluded && item.price === 0)
                            ? 'Included'
                            : formatCurrency(item.price, country)}
                        </ItemPrice>
                      </ItemPriceWrapper>
                      {onRemoveSoftwareItem &&
                        (item.price !== 0 ||
                          isTgpFoundationFamilyCode(item.productCode)) &&
                        !item.alreadyIncluded && (
                          <RemoveButton
                            onClick={e => {
                              e.stopPropagation()
                              onRemoveSoftwareItem(item, j, i)
                            }}
                            aria-label="Remove item"
                          >
                            <Icon name="close" size="xs" />
                          </RemoveButton>
                        )}
                    </ItemRow>
                    {itemAddons.length > 0 && (
                      <ItemDetailsPanel>
                        {itemAddons.map((addon, ai) => (
                          <ItemDetailRow key={addon.productCode || ai}>
                            <ItemDetailLabel>
                              {addon.productName}
                            </ItemDetailLabel>
                            <ItemDetailValue>
                              {addon.unitPrice == null || addon.unitPrice === 0
                                ? 'Included'
                                : formatCurrency(addon.unitPrice, country)}
                            </ItemDetailValue>
                          </ItemDetailRow>
                        ))}
                      </ItemDetailsPanel>
                    )}
                  </React.Fragment>
                )
              })}
            </SectionBlock>
          )}
        </SubAccordion>

        <SubAccordion>
          <SubAccordionHeader onClick={() => onToggleAddons?.(i)}>
            <SubAccordionMeta>
              <ChevronIcon open={addonsOpen[i]}>
                <Icon name="downArrow" size="xs" />
              </ChevronIcon>
              <div className="sub-category">
                Add-ons <span>- {location.addonItems.length} items</span>
              </div>
            </SubAccordionMeta>
            <SubAccordionTotal>
              {formatCurrency(
                location.addonItems.reduce((s, it) => {
                  const nestedTotal = (it.addons || []).reduce(
                    (as, a) =>
                      as +
                      (QUANTITY_AS_DOLLAR_CODES.has(a.productCode) &&
                      a.alreadyIncluded
                        ? Number(a.unitPrice ?? 0)
                        : Number(a.unitPrice ?? 0) * Number(a.quantity ?? 0)),
                    0,
                  )
                  return s + Number(it.price ?? 0) + nestedTotal
                }, 0),
                country,
              )}
            </SubAccordionTotal>
          </SubAccordionHeader>
          {addonsOpen[i] && location.addonItems.length > 0 && (
            <SectionBlock>
              {location.addonItems.map((item, j) => {
                const configEntries = Object.entries(
                  parseConfig(item.configuration),
                ).filter(([, v]) => v != null && v !== '')
                const itemAddons = item.addons || []
                const hasConfigDetails = configEntries.length > 0
                const detailKey = item.itemId || item.productCode || j
                const isDetailOpen = !!openAddonDetails[detailKey]
                return (
                  <React.Fragment key={j}>
                    <ItemRow
                      selected={item.selected}
                      crossedOut={item.crossedOut}
                    >
                      <ItemInfo>
                        <ItemName>{item.name}</ItemName>
                        <ItemSubRow>
                          <ItemSub>{item.sub}</ItemSub>
                          {hasConfigDetails && (
                            <ItemDetailsToggle
                              type="button"
                              onClick={e => {
                                e.stopPropagation()
                                toggleAddonDetail(detailKey)
                              }}
                            >
                              View details
                              <ChevronIcon open={isDetailOpen}>
                                <Icon name="downArrow" size="xs" />
                              </ChevronIcon>
                            </ItemDetailsToggle>
                          )}
                        </ItemSubRow>
                      </ItemInfo>
                      <ItemPriceWrapper>
                        <ItemPrice>
                          {item.price === null
                            ? ''
                            : item.price === 0 &&
                              item.productCode !== LEADS_LIFT_BNI_CODE &&
                              !item.showZeroPrice
                            ? 'Included'
                            : formatCurrency(item.price, country)}
                        </ItemPrice>
                      </ItemPriceWrapper>
                      {onRemoveAddonItem &&
                        !item.alreadyIncluded &&
                        !VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) &&
                        (item.price !== 0 ||
                          item.productCode === LEADS_LIFT_BNI_CODE ||
                          item.showZeroPrice ||
                          item.removable) && (
                          <RemoveButton
                            onClick={e => {
                              e.stopPropagation()
                              onRemoveAddonItem(item, j, i)
                            }}
                            aria-label="Remove item"
                          >
                            <Icon name="close" size="xs" />
                          </RemoveButton>
                        )}
                    </ItemRow>
                    {isDetailOpen && hasConfigDetails && (
                      <ItemDetailsPanel>
                        {configEntries.map(([k, v]) => (
                          <ItemDetailRow key={k}>
                            <ItemDetailLabel>
                              {DETAIL_LABELS[k] || k}
                            </ItemDetailLabel>
                            <ItemDetailValue>
                              {typeof v === 'object' && v !== null
                                ? String(v.value ?? JSON.stringify(v))
                                : String(v)}
                            </ItemDetailValue>
                          </ItemDetailRow>
                        ))}
                      </ItemDetailsPanel>
                    )}
                    {itemAddons.length > 0 && (
                      <ItemDetailsPanel>
                        {/* <ItemDetailAddonsHeading noDivider>
                        Add-Ons
                      </ItemDetailAddonsHeading> */}
                        {itemAddons.map((addon, ai) => (
                          <ItemDetailRow key={addon.productCode || ai}>
                            <ItemDetailLabel>
                              {addon.productName}
                            </ItemDetailLabel>
                            <ItemDetailValue>
                              {addon.unitPrice == null || addon.unitPrice === 0
                                ? 'Included'
                                : formatCurrency(
                                    QUANTITY_AS_DOLLAR_CODES.has(
                                      addon.productCode,
                                    ) && addon.alreadyIncluded
                                      ? addon.unitPrice
                                      : addon.unitPrice * addon.quantity,
                                    country,
                                  )}
                            </ItemDetailValue>
                          </ItemDetailRow>
                        ))}
                      </ItemDetailsPanel>
                    )}
                  </React.Fragment>
                )
              })}
            </SectionBlock>
          )}
        </SubAccordion>

        <SubAccordion>
          <SubAccordionHeader onClick={() => onToggleOneTimeFee?.(i)}>
            <SubAccordionMeta>
              <ChevronIcon open={oneTimeFeeOpen[i]}>
                <Icon name="downArrow" size="xs" />
              </ChevronIcon>
              <div className="sub-category">
                One Time Fee{' '}
                <span>- {(location.oneTimeFeeItems ?? []).length} items</span>
              </div>
            </SubAccordionMeta>
            <SubAccordionTotal>
              {formatCurrency(
                (location.oneTimeFeeItems ?? []).reduce(
                  (s, it) => s + Number(it.price ?? 0),
                  0,
                ),
                country,
              )}
            </SubAccordionTotal>
          </SubAccordionHeader>
          {oneTimeFeeOpen[i] && (location.oneTimeFeeItems ?? []).length > 0 && (
            <SectionBlock>
              {(location.oneTimeFeeItems ?? []).map((item, j) => (
                <ItemRow
                  key={j}
                  selected={item.selected}
                  crossedOut={item.crossedOut}
                >
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemSub>{item.sub}</ItemSub>
                  </ItemInfo>
                  <ItemPriceWrapper>
                    <ItemPrice>
                      {item.price === null
                        ? ''
                        : formatCurrency(item.price, country)}
                    </ItemPrice>
                  </ItemPriceWrapper>
                  {onRemoveOneTimeFeeItem &&
                    item.removable &&
                    !VERTICAL_CLIENTSVCS_CODES.includes(item.productCode) && (
                      <RemoveButton
                        onClick={e => {
                          e.stopPropagation()
                          onRemoveOneTimeFeeItem(item, j, i)
                        }}
                        aria-label="Remove item"
                      >
                        <Icon name="close" size="xs" />
                      </RemoveButton>
                    )}
                </ItemRow>
              ))}
            </SectionBlock>
          )}
        </SubAccordion>

        {locationCoupons.length > 0 && activeTab !== 'monthly' && (
          <SubAccordion>
            <SubAccordionHeader onClick={() => onToggleCoupons?.(i)}>
              <SubAccordionMeta>
                <ChevronIcon open={couponsOpen[i]}>
                  <Icon name="downArrow" size="xs" />
                </ChevronIcon>
                <div className="sub-category">
                  Coupons & Discounts{' '}
                  <span>- {locationCoupons.length} items</span>
                </div>
              </SubAccordionMeta>
            </SubAccordionHeader>
            {couponsOpen[i] && (
              <SectionBlock>
                {locationCoupons.map((coupon, j) => (
                  <ItemRow key={j}>
                    <ItemInfo>
                      <ItemName>{coupon.code}</ItemName>
                      <ItemSub>{coupon.description}</ItemSub>
                    </ItemInfo>
                    <ItemPrice>
                      {coupon.isPercentDiscount
                        ? `-${coupon.discountPercent}%`
                        : `-${formatCurrency(coupon.discountAmount, country)}`}
                    </ItemPrice>
                    {onRemoveCoupon && (
                      <RemoveButton
                        onClick={e => {
                          e.stopPropagation()
                          onRemoveCoupon(coupon, j, i)
                        }}
                        aria-label="Remove coupon"
                      >
                        <Icon name="close" size="xs" />
                      </RemoveButton>
                    )}
                  </ItemRow>
                ))}
              </SectionBlock>
            )}
          </SubAccordion>
        )}
      </>
    )
  }

  return (
    <Wrapper flushEdges={flushEdges}>
      <Header>
        <HeaderLeft>
          <h2>Order Summary</h2>
          <p>{totalItems} Items</p>
        </HeaderLeft>
        <HeaderRight>
          <div className="total">Total - {formatCurrency(total, country)}</div>
          <div className="note">
            *
            {isAus
              ? 'excludes incentives'
              : isNZ
              ? 'excludes fees and incentives'
              : 'excludes taxes, fees and incentives'}
          </div>
        </HeaderRight>
      </Header>

      {hasItems ? (
        <>
          <GradientTabs activeTab={activeTab} onChange={onTabChange}>
            {tabs.map(tab => (
              <GradientTab key={tab.id} id={tab.id}>
                <div className={tab.accent ? 'amount blue' : 'amount'}>
                  {formatCurrency(
                    tabTotals?.[tab.id] ??
                      (tab.id === 'today' ? discountedTotal : total),
                    country,
                  )}{' '}
                  <span>{getTabSuffix(tab)}</span>
                </div>
                <div className={tab.accent ? 'label blue' : 'label'}>
                  {tab.label}{' '}
                  <span>({tabItemCounts?.[tab.id] ?? totalItems} Items)</span>
                </div>
              </GradientTab>
            ))}
          </GradientTabs>

          {singleLocation ? (
            <LocationAccordion>
              {locations[0] && renderSubAccordions(locations[0], 0)}
            </LocationAccordion>
          ) : (
            <LocationAccordion>
              {locations.map((location, i) => (
                <LocationAccordionWrapper key={i}>
                  <LocationHeader
                    onClick={() => location.isLoaded && onToggleLocation?.(i)}
                  >
                    <LocationMeta>
                      <span className="pin">
                        <Icon name="loc" size={12} />
                      </span>
                      <span className="loc-name">{location.name}</span>
                      <span className="loc-count">
                        {' '}
                        -{' '}
                        {location.isLoaded
                          ? `${location.itemCount} items`
                          : 'Loading…'}
                      </span>
                    </LocationMeta>
                    <span className="loc-total">
                      {location.isLoaded &&
                        formatCurrency(
                          [
                            ...(location.softwareItems ?? []),
                            ...(location.addonItems ?? []),
                            ...(location.oneTimeFeeItems ?? []),
                          ].reduce((s, it) => s + Number(it.price ?? 0), 0),
                          country,
                        )}
                      {location.isLoaded && (
                        <ChevronIcon open={openLocations[i]}>
                          <Icon name="downArrow" size="xs" />
                        </ChevronIcon>
                      )}
                    </span>
                  </LocationHeader>

                  {location.isLoaded &&
                    openLocations[i] &&
                    renderSubAccordions(location, i)}
                </LocationAccordionWrapper>
              ))}
            </LocationAccordion>
          )}

          {isAus && (
            <GstDisclaimer>
              {'*All plans include GST'}
              {!isUpgrade && hasSeoItem && ' except SEO'}
            </GstDisclaimer>
          )}

          {footer && <FooterRow>{footer}</FooterRow>}
        </>
      ) : isSingleLocationLoading ? (
        <EmptyState>
          <p>Loading…</p>
        </EmptyState>
      ) : (
        <EmptyState>
          <Icon name="noOrder" size={93} />
          <p>No Products Added</p>
          <span>Select from catalog</span>
        </EmptyState>
      )}
    </Wrapper>
  )
}
